import { calculateProductPricing } from '../../../../uniform/components/pricing-calculator/src/calculate/calculate-product-pricing';
import { getProductCalculationInputs } from '../../../../uniform/components/pricing-calculator/src/calculate/get-product-calculation-inputs';

import { getProductPricingConfig, type ProductPricingConfig } from './product-pricing-configs';

import type { Product, ProductKey } from './types';
import type { Config } from '../../../../uniform/components/pricing-calculator/src/types';

const MONTHS_PER_YEAR = 12;

export interface CalculateProductsResult {
	byProductKey: Partial<Record<ProductKey, number>>;
}

export interface CalculateProductsArgs extends Pick<
	Config,
	'ccpPricingData' | 'currencyISOCode' | 'localeISOCode'
> {
	products: Product[];
}

export function calculateMonthlyPerUnitPrices(
	args: CalculateProductsArgs,
): CalculateProductsResult {
	const { products, ccpPricingData, currencyISOCode, localeISOCode } = args;

	const byProductKey: Partial<Record<ProductKey, number>> = {};

	for (const product of products) {
		const def: ProductPricingConfig = getProductPricingConfig(product.productKey);
		const { edition, unitCount, billingCycle } = product.inputs;
		const selectedSku = def.editionToSku[edition];

		const calcInputs = getProductCalculationInputs(
			def.planLimitConfigs,
			def.unitMinEnterprise,
			unitCount,
			billingCycle,
		);

		const avgPerUnitPrice = calculateProductPricing(
			ccpPricingData,
			calcInputs,
			currencyISOCode,
			localeISOCode,
		).find((r) => r.offeringSku === selectedSku)?.avgPerUnitPrice;

		if (avgPerUnitPrice === undefined) {
			continue;
		}

		// The calculator's per-unit figure is annual for ANNUAL billing.
		byProductKey[product.productKey] =
			billingCycle === 'ANNUAL' ? avgPerUnitPrice / MONTHS_PER_YEAR : avgPerUnitPrice;
	}

	return { byProductKey };
}

export function collectAllRegisteredSkus(pricingConfigs: ProductPricingConfig[]): string[] {
	const set = new Set<string>();
	for (const cfg of pricingConfigs) {
		for (const sku of cfg.offeringSkus) {
			set.add(sku);
		}
	}
	return [...set];
}
